<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const scrolled = ref(false)
const mobileMenuOpen = ref(false)
const productsMenuOpen = ref(false)

const handleScroll = () => {
  scrolled.value = window.scrollY > 50
}

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
    // Update URL hash to trigger :target pseudo-class
    window.history.pushState(null, '', `#${sectionId}`)
    mobileMenuOpen.value = false
    productsMenuOpen.value = false
  }
}

const toggleProductsMenu = () => {
  productsMenuOpen.value = !productsMenuOpen.value
}

const handleClickAway = (event) => {
  // Close products dropdown if clicking outside
  if (productsMenuOpen.value && !event.target.closest('.relative')) {
    productsMenuOpen.value = false
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  document.addEventListener('click', handleClickAway)
  // Clear any hash on page load to prevent default selection
  if (window.location.hash) {
    window.history.replaceState(null, '', window.location.pathname)
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('click', handleClickAway)
})
</script>

<template>
  <nav
    class="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
    :class="scrolled ? 'bg-white shadow-md' : 'bg-transparent'"
  >
    <div class="section-container">
      <div class="flex items-center justify-between h-16">
        <!-- Logo and Brand -->
        <div class="flex items-center space-x-3">
          <img src="/img/kannister.png" alt="Kannister Logo" class="h-10 w-10" />
          <span class="text-xl font-bold text-kannister-dark-900">Kannister</span>
        </div>

        <!-- Desktop Navigation -->
        <div class="hidden md:flex items-center space-x-8">
          <div class="relative">
            <button
              @click="toggleProductsMenu"
              class="text-kannister-dark-700 hover:text-kannister-blue-600 transition-colors flex items-center space-x-1"
            >
              <span>Products</span>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <!-- Products Dropdown -->
            <div
              v-if="productsMenuOpen"
              class="absolute top-full mt-2 left-0 w-48 bg-white border border-kannister-dark-200 rounded-lg shadow-lg py-2 z-50"
            >
              <a
                href="#kannister"
                @click="productsMenuOpen = false"
                class="block w-full text-left px-4 py-2 text-kannister-dark-700 hover:bg-kannister-dark-50 transition-colors"
              >
                Kannister
              </a>
              <a
                href="#krate"
                @click="productsMenuOpen = false"
                class="block w-full text-left px-4 py-2 text-kannister-dark-700 hover:bg-kannister-dark-50 transition-colors"
              >
                Krate
              </a>
              <a
                href="#enterprise-server"
                @click="productsMenuOpen = false"
                class="block w-full text-left px-4 py-2 text-kannister-dark-700 hover:bg-kannister-dark-50 transition-colors"
              >
                Enterprise Server
              </a>
            </div>
          </div>
          <a
            href="#"
            class="text-kannister-dark-700 hover:text-kannister-blue-600 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            Documentation
          </a>
          <a
            href="https://github.com/kannister-app"
            class="text-kannister-dark-700 hover:text-kannister-blue-600 transition-colors flex items-center space-x-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path fill-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clip-rule="evenodd" />
            </svg>
            <span>GitHub</span>
          </a>
          <button
            @click="scrollToSection('waitlist')"
            class="btn-primary"
          >
            Join Waitlist
          </button>
        </div>

        <!-- Mobile Menu Button -->
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="md:hidden text-kannister-dark-700"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              v-if="!mobileMenuOpen"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
            <path
              v-else
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Mobile Menu -->
      <div
        v-if="mobileMenuOpen"
        class="md:hidden py-4 space-y-3 border-t border-kannister-dark-200"
      >
        <a
          href="#kannister"
          @click="mobileMenuOpen = false"
          class="block w-full text-left px-4 py-2 text-kannister-dark-700 hover:bg-kannister-dark-50"
        >
          Kannister
        </a>
        <a
          href="#krate"
          @click="mobileMenuOpen = false"
          class="block w-full text-left px-4 py-2 text-kannister-dark-700 hover:bg-kannister-dark-50"
        >
          Krate
        </a>
        <a
          href="#enterprise-server"
          @click="mobileMenuOpen = false"
          class="block w-full text-left px-4 py-2 text-kannister-dark-700 hover:bg-kannister-dark-50"
        >
          Enterprise Server
        </a>
        <a
          href="#"
          class="block px-4 py-2 text-kannister-dark-700 hover:bg-kannister-dark-50"
        >
          Documentation
        </a>
        <a
          href="https://github.com/kannister-app"
          class="block px-4 py-2 text-kannister-dark-700 hover:bg-kannister-dark-50"
          target="_blank"
          rel="noopener noreferrer"
        >
          GitHub
        </a>
        <button
          @click="scrollToSection('waitlist')"
          class="block w-full text-left px-4 py-2 text-kannister-blue-600 font-medium"
        >
          Join Waitlist
        </button>
      </div>
    </div>
  </nav>
</template>
